import { Curve3, Vector3 } from '@babylonjs/core/Maths/math';
import { eConnectorPositionMode, eSketchPathType } from '@kb3d/models/classes/enums';
import { SketchControlPoint } from '@kb3d/models/classes/features/sketch-control-point';
import { SketchNode } from '@kb3d/models/classes/meshes/sketch-node';
import { SketchPath } from '@kb3d/models/classes/meshes/sketch-node-path';
import { IKbVectorLike, KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { IBoundingBox } from '@kb3d/models/classes/primitives/primitive-utilities';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass, isTrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { SketchControlPointRenderer } from '../connectors/sketch-control-point';
import { ChangeMap } from '../renderer-manager';
import { Processor } from './processor';

/**
 * Processor to calculate the bounding box of a sketch, since it is determined by the path defined within.
 * Must run after the mate processor because mates can move sketch points and change the bounding box.
 */
export class SketchProcessor extends Processor {
    constructor(private controlPointRenderer: SketchControlPointRenderer) {
        super();
    }

    beforeRender() {}

    beforeDeletions(deletions: ChangeMap) {
        // Update paths that need to be updated
        if (deletions.has(Token.SketchPath)) {
            const sketchPaths = deletions.get(Token.SketchPath) as Array<ITrackedClass<SketchPath>>;
            sketchPaths.forEach(p => this.updatePath(p));
        }

        if (deletions.has(Token.SketchControlPoint) || deletions.has(Token.SketchPath)) {
            const sketchPathChanges = deletions.get(Token.SketchPath) as Array<ITrackedClass<SketchPath>>;
            const sketchControlPointChanges = deletions.get(Token.SketchControlPoint) as Array<
                ITrackedClass<SketchControlPoint>
            >;
            const sketchesToUpdate = new Map<string, SketchNode>();
            sketchPathChanges.forEach(node => {
                const sketch = node._parent;
                if (sketch) {
                    if (!sketchesToUpdate.has(sketch._dynamicId)) {
                        sketchesToUpdate.set(sketch._dynamicId, sketch);
                    }
                }
            });
            sketchControlPointChanges.forEach(node => {
                const sketch = node.getSketch();
                if (sketch) {
                    if (!sketchesToUpdate.has(sketch._dynamicId)) {
                        sketchesToUpdate.set(sketch._dynamicId, sketch);
                    }
                }
            });

            sketchesToUpdate.forEach(this.calculateSketchBounds);
        }
    }

    beforeUpdates(changes: ChangeMap) {
        // Update paths that need to be updated
        if (changes.has(Token.SketchPath)) {
            const sketchPaths = changes.get(Token.SketchPath) as Array<ITrackedClass<SketchPath>>;
            sketchPaths.forEach(p => this.updatePath(p));
        }

        // Update the sketch bounding box with the path control points within it that are absolute positioned
        if (changes.has(Token.SketchControlPoint)) {
            const sketchControlPointChanges = changes.get(Token.SketchControlPoint) as Array<
                ITrackedClass<SketchControlPoint>
            >;
            const sketchesToUpdate = new Map<string, SketchNode>();
            sketchControlPointChanges.forEach(node => {
                if (node.changes.hasAny('position', 'positionMode')) {
                    const sketch = node.getSketch();
                    if (sketch) {
                        if (!sketchesToUpdate.has(sketch._dynamicId)) {
                            sketchesToUpdate.set(sketch._dynamicId, sketch);
                        }
                    }
                }
            });

            sketchesToUpdate.forEach(this.calculateSketchBounds);
        }
    }

    afterUpdates(changes: ChangeMap): void {
        if (changes.has(Token.SketchControlPoint)) {
            const sketchControlPointChanges = changes.get(Token.SketchControlPoint) as Array<
                ITrackedClass<SketchControlPoint>
            >;
            sketchControlPointChanges.forEach(node => {
                const sketch = node.getSketch();
                if (sketch) {
                    this.afterCleanup(() => {
                        sketch.paths.forEach(path => {
                            if (isTrackedClass(path)) {
                                this.updatePath(path, true);
                            }
                        });
                        sketch.connectors.forEach(connector => {
                            if (connector.positionMode === eConnectorPositionMode.moveAlongSketch) {
                                if (connector._parent instanceof SketchNode) {
                                    connector._sketchControlPoints = this.getControlPoints(connector._parent);
                                }
                            }
                        });
                    });
                }
            });
        }
    }

    calculateSketchBounds(sketch: SketchNode) {
        const controlPoints = sketch.paths.flatMap(path => path.controlPoints);
        let min: Vector3 | undefined;
        let max: Vector3 | undefined;

        controlPoints.forEach(controlPoint => {
            if (controlPoint.positionMode === eConnectorPositionMode.absolute) {
                const position = controlPoint.position;
                if (!min) {
                    min = position.toVec3();
                }

                max = position.toVec3();
            }
        });

        if (min && max) {
            sketch._boundingBoxMin = KbVector.FromVec3(min);
            sketch._boundingBoxMax = KbVector.FromVec3(max);
        } else {
            sketch._boundingBoxMin = KbVector.Zero();
            sketch._boundingBoxMax = KbVector.Zero();
        }
        sketch._boundingBoxChanges.next(sketch.getBoundingBox());
    }

    private updatePath(path: ITrackedClass<SketchPath>, force?: boolean) {
        let points: Vector3[] | undefined;

        if (
            force ||
            path.changes.hasAny('controlPoints', 'tessellation', 'type', 'holeForPathGroup', 'pathGroupKey') ||
            !path._points?.length
        ) {
            points = this.buildSketchPoints(path);
            path._points = points;

            const parent = path._parent;
            if (parent instanceof SketchNode && isTrackedClass(parent)) {
                parent.setPropertyIsModified('paths', true);
            }
        } else if (path.changes.has('_selected')) {
            const parent = path._parent;
            if (parent instanceof SketchNode && isTrackedClass(parent)) {
                parent.setPropertyIsModified('paths', true);
            }
        }
    }

    private buildSketchPoints(path: SketchPath) {
        const points: Vector3[] = [];
        if (!path._parent) {
            return points;
        }
        const bb = path._parent.getBoundingBox();

        switch (path.type) {
            case eSketchPathType.catmullRom: {
                const positions = path.controlPoints.map(v => this.getPointPosition(v));
                if (positions.length > 2) {
                    const curve = Curve3.CreateCatmullRomSpline(positions, path.tessellation, false);
                    points.push(...curve.getPoints());
                } else {
                    points.push(...positions);
                }

                break;
            }
            case eSketchPathType.hermite: {
                for (let i = 0; i + 1 < path.controlPoints.length; i++) {
                    const start = path.controlPoints[i],
                        end = path.controlPoints[i + 1];
                    const p0 = this.getPointPosition(start);
                    const p1 = this.getPointPosition(end);
                    const d0 = this.getPointDirection(start, bb);
                    const d1 = this.getPointDirection(end, bb);

                    const curve = Curve3.CreateHermiteSpline(p0, d0, p1, d1, path.tessellation);
                    points.push(...curve.getPoints());
                }
                break;
            }
            case eSketchPathType.cubicBezier: {
                for (let i = 0; i + 1 < path.controlPoints.length; i++) {
                    const start = path.controlPoints[i],
                        end = path.controlPoints[i + 1];
                    const direction2 = this.getPointDirection2(end, bb);
                    if (direction2) {
                        const p0 = this.getPointPosition(start);
                        const p3 = this.getPointPosition(end);
                        if (p0.equals(p3)) {
                            continue;
                        }

                        const p1 = p0.add(this.getPointDirection(start, bb));
                        const p2 = p3.add(direction2);
                        const curve = Curve3.CreateCubicBezier(p0, p1, p2, p3, path.tessellation);
                        points.push(...curve.getPoints());
                    }
                }
                break;
            }
            case eSketchPathType.quadraticBezier: {
                const controlPoint = new Vector3();
                for (let i = 0; i + 1 < path.controlPoints.length; i++) {
                    const start = path.controlPoints[i],
                        end = path.controlPoints[i + 1];
                    const p0 = this.getPointPosition(start);
                    const p2 = this.getPointPosition(end);
                    if (p0.equals(p2)) {
                        continue;
                    }

                    p0.addToRef(this.getPointDirection(start, bb), controlPoint);

                    const curve = Curve3.CreateQuadraticBezier(p0, controlPoint, p2, path.tessellation);
                    points.push(...curve.getPoints());
                }
                break;
            }
            case eSketchPathType.centerRadiusCircle: {
                if (path.controlPoints.length >= 2) {
                    const [p0, p1] = path.controlPoints.slice(0, 2).map(cp => this.getPointPosition(cp));
                    const normal = this.getPointDirection(path.controlPoints[0], bb).normalize();
                    const p2 = Vector3.Cross(p0.subtract(p1), normal).addInPlace(p0);
                    const curve = Curve3.ArcThru3Points(
                        p0.scale(2).subtractInPlace(p1),
                        p1,
                        p2,
                        path.tessellation * 2,
                        false,
                        true
                    );
                    points.push(...curve.getPoints());
                }
                break;
            }
            case eSketchPathType.arcThrough3Points: {
                if (path.controlPoints.length >= 3) {
                    const [p0, p1, p2] = path.controlPoints.slice(0, 3).map(cp => this.getPointPosition(cp));
                    const curve = Curve3.ArcThru3Points(p0, p1, p2, path.tessellation, false, false);
                    points.push(...curve.getPoints());
                }
                break;
            }

            default: {
                path.controlPoints.forEach(point => {
                    points.push(this.getPointPosition(point));
                });
            }
        }

        // /**
        //  * We want the sketch to always "face" in the positive direction of the axis for consistency
        //  * Otherwise it's indeterminate which way the sketch will face and makes the sketch hard to manage
        //  * because it depends on the handed-ness of the points (clockwise or counter-clockwise)
        //  */
        // const primaryNormal = axisToVector3(path._parent.normal);
        // const faceNormal = calculateFaceNormal(points);
        // if (Vector3.Dot(faceNormal, primaryNormal) < 0) {
        //     points.reverse();
        // }

        return points;
    }

    private getPointPosition(point: SketchControlPoint) {
        return this.controlPointRenderer.getControlPointAbsPosition(point);
    }

    private getPointDirection(point: SketchControlPoint, bb: IBoundingBox) {
        return this.controlPointRenderer.getRelativeDirection(point, point.direction, bb);
    }
    private getPointDirection2(point: SketchControlPoint, bb: IBoundingBox) {
        if (!point.direction2) {
            return undefined;
        } else {
            return this.controlPointRenderer.getRelativeDirection(point, point.direction2, bb);
        }
    }

    private getControlPoints(node: SketchNode) {
        const controlPoints: IKbVectorLike[] = [];
        node.paths.forEach(path => {
            path._points.forEach(point => {
                controlPoints.push(point);
            });
        });
        return controlPoints;
    }
}
